<script lang="ts">
import { defineComponent, PropType, ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { newUserSessionPath, newUserRegistrationPath } from '@/generated/railsRoutes'
import GlobalNavigation from '@/components/_core/GlobalNavigation.vue'
import DrawerNavigation from '@/components/_core/DrawerNavigation.vue'
import LogoBizHint from '@/components/logo/LogoBizHintDefault.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import SearchWord from '@/components/search/SearchWord.vue'
import SearchWordWrapperForSP from '@/components/search/SearchWordWrapperForSP.vue'
import ActivityForHeader from '@/components/activity/ActivityForHeader.vue'
import UserNavigationForHeader from '@/components/user/UserNavigationForHeader.vue'
import stateInitializer from '@/models/stateInitializer'
import { useHeaderStore } from '@/stores/header'
import { useUserStore } from '@/stores/user'
import { useDrawerNavigationStore } from '@/stores/drawerNavigation'
import scrollUtil from '@/utilities/scrollUtil'

export default defineComponent({
  name: 'HeaderGlobal',

  components: {
    GlobalNavigation,
    DrawerNavigation,
    LogoBizHint,
    ButtonSubmit,
    SearchWord,
    SearchWordWrapperForSP,
    ActivityForHeader,
    UserNavigationForHeader,
  },

  props: {
    /**
     * ヘッダーのグローバルナビゲーションを表示するかどうか
     */
    hasGlobalNavigation: {
      type: Boolean,
      default: true,
    },

    /**
     * ヘッダーの検索フォームを表示するかどうか
     */
    hasSearchForm: {
      type: Boolean,
      default: true,
    },

    logoColor: {
      type: String as PropType<'default' | 'white' | 'gray'>,
      default: 'default',
    },
  },

  setup() {
    // store
    const headerStore = useHeaderStore()
    const userStore = useUserStore()
    const drawerNavigationStore = useDrawerNavigationStore()

    const header = ref()

    const isOpenActivity = ref(false)
    const isOpenSearchWordForSP = ref(false)
    const mounted = ref(false)

    interface State {
      themes: ThemeItem[],
      selectedThemeId: Maybe<number>,
      keywords: {label: string, to: string}[],
      searchWord: {
        input: InputText,
      },
    }
    const state: State = reactive({
      themes: [],
      selectedThemeId: null,
      keywords: [],
      searchWord: {
        input: {
          value: '',
          name: '',
        },
      },
    })
    stateInitializer(state, 'header')

    onMounted(() => {
      // teleport 利用するための onMounted 完了フラグを立てる
      mounted.value = true

      if (header.value) {
        headerStore.setHeaderElement(header.value)
      }
    })

    // スクロールイベントによるヘッダーの表示制御
    let isShowHeader = false
    const cleanupScroll = scrollUtil.throttle({
      callback: (e = {
        prevScrollPos: 0,
        updateScrollPos: 0,
        isUp: false,
        isDown: false,
        isPageLoad: false,
      }) => {
        if (e.isPageLoad) return
        if (e.prevScrollPos !== e.updateScrollPos) {
          /**
           * 上スクロールの場合はヘッダーを表示
           * 下スクロールの場合はヘッダーを非表示
           *
           * @description スマホの上スクロール時に、コンテンツ上端に達した際のストレッチオーバースクロール時の戻り下スクロールイベントで、
           *              ヘッダー非表示にされる（ ≒ 画面上端ではほぼ確実にヘッダーが非表示になる）ことを防ぐため、update scroll position が 10px 未満の場合は強制的にヘッダー表示状態にする
           */
          isShowHeader = e.updateScrollPos !== undefined && e.updateScrollPos < 40
            ? true
            : !!e.isUp
          headerStore.updateShowStatus(isShowHeader)

          // スクロールイベントが発生した際には、スクロール方向に関わらず強制的に「アクティビティ」を閉じる
          isOpenActivity.value = false
        }
      },
      wait: 30,
    })

    onBeforeUnmount(() => {
      cleanupScroll()
    })

    const openSearchWord = () => {
      isOpenSearchWordForSP.value = true
    }

    return {
      // routes
      newUserSessionPath,
      newUserRegistrationPath,

      // store
      userStore,
      headerStore,
      drawerNavigationStore,

      // data
      state,
      header,
      isOpenActivity,
      isOpenSearchWordForSP,
      mounted,

      // methods
      openSearchWord,
    }
  }
})
</script>

<template>
  <header
    ref="header"
    :class="[
      'f-header',
      { 'is-active': headerStore.context.isShow },
    ]">
    <div class="f-header--body is-primary">
      <div class="l-cols__header">
        <!-- ロゴ -->
        <div class="l-cols__header--col is-logo">
          <LogoBizHint
            :additional-style-class="['is-header']"
            :type="logoColor" />
        <!-- /.l-cols__header col --></div>

        <!-- PC 用ワード検索フォーム -->
        <div class="l-cols__header--col is-searchPC">
          <!--
            hasSearchForm の値で、col ごと v-if で消したいが、
            スタイルの関係で残す。（searchForm がない場合に、右側のナビゲーションがロゴの脇に詰まってしまい、その対策でロゴカラムに flex-grow 設定したとしても、検索フォームが存在する場合に幅計算が狂ってしまう。）
          -->
          <SearchWord
            v-if="hasSearchForm"
            v-model="state.searchWord.input.value"
            :name="state.searchWord.input.name"
            max-width="280px" />
        <!-- /.l-cols__header col --></div>

        <!-- SP 用ワード検索フォーム -->
        <div
          v-if="hasSearchForm"
          class="l-cols__header--col is-searchSP">
          <div class="o-action__open is-searchWord">
            <button
              class="o-action__open--item"
              @click="openSearchWord">
              ワード検索を開く
            </button>
          <!-- /.o-action__open --></div>

          <!--
            SP 用の検索フォームは、スクリーンロックレイヤーの上に配置されるので、
            スクリーンロックレイヤーより大きい z-index としたいが、
            header 要素配下にいると、親の z-index 値にレンダリングフローが依存するため、
            vue teleport でレンダリング場所を移動させる。
            @see https://ja.vuejs.org/guide/built-ins/teleport

            ※ teleport 先が Vue インスタンスに紐づく DOM のため、
              onMounted で DOM が生成されたのを確認してからレンダリングする
          -->
          <template v-if="mounted">
            <teleport to="#global-container">
              <SearchWordWrapperForSP v-model:is-open="isOpenSearchWordForSP">
                <SearchWord
                  v-model="state.searchWord.input.value"
                  :name="state.searchWord.input.name" />
              </SearchWordWrapperForSP>
            </teleport>
          </template>
        <!-- /.l-cols__header col --></div>

        <template v-if="!userStore.context.isSignedIn">
          <!-- ログイン -->
          <div class="l-cols__header--col is-signIn">
            <div class="o-action__signIn">
              <a
                class="o-action__signIn--item"
                :href="newUserSessionPath()">
                ログイン
              </a>
            </div>
          <!-- /.l-cols__header col --></div>

          <!-- 会員登録 -->
          <div class="l-cols__header--col is-signUp">
            <ButtonSubmit
              :href="newUserRegistrationPath()"
              :is-font-small="true">
              無料登録
            </ButtonSubmit>
          <!-- /.l-cols__header col --></div>
        </template>

        <template v-else>
          <!-- お知らせ -->
          <div class="l-cols__header--col is-activity">
            <ActivityForHeader v-model:is-open="isOpenActivity" />
          <!-- /.l-cols__header col --></div>

          <!-- ユーザーメニュー -->
          <div class="l-cols__header--col is-user">
            <UserNavigationForHeader />
          <!-- /.l-cols__header col --></div>
        </template>

        <!-- SP 時のハンバーガーボタン -->
        <div class="l-cols__header--col is-hamburger">
          <div class="o-action__hamburger">
            <button
              class="o-action__hamburger--item"
              @click="drawerNavigationStore.showDrawerNavigation">
              {{ drawerNavigationStore.context.isShow ? 'ナビゲーションを閉じる' : 'ナビゲーションを開く' }}
            </button>
          <!-- /.o-action__hamburger --></div>

          <!--
            SP 用のドロワーナビゲーション

            SP 用のドロワーナビは、スクリーンロックレイヤーの上に配置されるので、
            スクリーンロックレイヤーより大きい z-index としたいが、
            header 要素配下にいると、親の z-index 値にレンダリングフローが依存するため、
            vue teleport でレンダリング場所を移動させる。
            @see https://ja.vuejs.org/guide/built-ins/teleport

            ※ teleport 先が Vue インスタンスに紐づく DOM のため、
              onMounted で DOM が生成されたのを確認してからレンダリングする
          -->
          <template v-if="mounted">
            <teleport to="#global-container">
              <DrawerNavigation
                :items="state.themes"
                :keywords="state.keywords" />
            </teleport>
          </template>
        <!-- /.l-cols__header col --></div>
      <!-- /.l-cols__header --></div>
    <!-- /.f-header body --></div>

    <div
      v-if="hasGlobalNavigation"
      class="f-header--body is-secondary">
      <GlobalNavigation
        :items="state.themes"
        :selected-theme-id="state.selectedThemeId" />
    <!-- /.f-header body --></div>
  </header>
</template>

<style lang="sass" scoped>
// Foundation
.f-header
  $root: &
  width: 100%
  min-width: #{$global-contents-width-pc}px
  position: fixed
  top: -#{$header-height-pc}px
  left: 0
  background-color: rgba($bg-white, 1)
  box-shadow: $drop-bottom-shadow
  z-index: 5
  +transition($duration: $transition-middle)

  &.is-active
    top: 0

  & &--body
    position: relative

    &.is-primary
      width: 100%
      max-width: #{$global-contents-width-pc}px
      height: #{$header-primary-height-pc}px
      margin: 0 auto

  +breakpoint($max-media: tablet)
    min-width: auto

    #{$root}--body
      &.is-primary
        height: #{$header-primary-height-sp}px
        padding: 0 #{$global-prop-sp}px

        &:not(:last-child)
          box-shadow: $drop-bottom-shadow

// Layout
.l-cols__header
  $root: &
  height: 100%
  display: flex
  align-items: center

  & &--col
    &.is-logo
      width: 100%
      max-width: 150px
      line-height: 1

    &.is-searchPC
      flex-grow: 1
      margin-left: 24px

    &.is-searchSP
      display: none

    &.is-signIn
      width: 100px

    &.is-user
      margin-left: 16px

    &.is-hamburger
      display: none

  +breakpoint($max-media: tablet)
    justify-content: flex-end

    #{$root}--col
      &.is-logo
        width: auto
        max-width: none
        flex-grow: 1

      &.is-searchSP
        display: block

      &.is-searchPC,
      &.is-signIn,
      &.is-signUp,
      &.is-user
        display: none

      &.is-hamburger
        display: block

// Object
.o-action
  &__signIn
    & &--item
      display: block
      color: $font-black
      font-size: $font-small
      text:
        align: center
        decoration: none

  &__hamburger
    & &--item
      $button-size: 40px
      width: $button-size
      height: #{$header-primary-height-sp}px
      display: block
      position: relative
      overflow: hidden
      line-height: #{$header-primary-height-sp}px
      color: rgba($font-white, 0)
      white-space: nowrap

      &::before
        content: ""
        width: $button-size
        height: $button-size
        display: block
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        background:
          image: url(@/images/icon/icon-hamburger_01.svg)

  &__open
    $root: &

    &.is-searchWord
      $button-size: 40px
      #{$root}--item
        width: $button-size
        height: $button-size
        display: block
        position: relative
        overflow: hidden
        color: rgba($font-white, 0)
        white-space: nowrap

        &::before
          content: ""
          width: $button-size
          height: $button-size
          display: block
          position: absolute
          top: 50%
          left: 50%
          transform: translate(-50%, -50%)
          background:
            image: url(@/images/icon/icon-search_02.svg)
            position: center center
            repeat: no-repeat
</style>
